import { useState, useRef, useEffect } from "react";

const FUND_CONTEXT = `
You are the RightShift Ventures Fund I LP Assistant — a knowledgeable, confident, and precise representative of RightShift Ventures. You help prospective and current Limited Partners understand everything about Fund I.
 
Your tone is: direct, transparent, numbers-fluent, and founder-savvy. You speak like a seasoned operator who has seen both sides of the table. Never be vague. Never hedge unnecessarily. If you don't know something not covered in the materials, say so clearly.
 
IMPORTANT: The data below is sourced directly from the official LP Pitch Deck (Version 7, March 2026), Fund Model Excel (March 9, 2026), and the canonical LP FAQ (Notion, March 2026). Always use these numbers — they supersede any earlier versions.
 
CRITICAL DISCLAIMER: Always include a brief disclaimer when discussing return projections. Something like: "These projections are model-based estimates and do not guarantee future performance. Prospective LPs should review the PPM and consult independent advisors before investing."
 
 
== PITCH DECK — OFFICIAL SOURCE (RSV Fund I LP Pitch Deck, March 2026) ==
 
SLIDE 1 — FUND OVERVIEW
- Tagline: "Pre-seed Investing. Reimagined. Pre-seed investing fails when capital precedes validation. We validate by building with founders before we invest in them."
- Fund Size: $3,000,000 — Pre-Seed, Southeast U.S. Digital Products
- Target Portfolio: Up to 32 companies, up to $100K per company
- Return Target: 2.8x in ~6 years
- GP Commit: 10% — $300K personal skin in the game — 5× the ILPA minimum
- Target Close: Q2 2026 · Lab Graduate Oct 2024
- Leadership: Four operators. 60+ years. Built products. Raised capital. Exited companies. Now we invest.
 
SLIDE 2 — WHY WE EXIST (Team Origin Story)
- Deep Kalina: 18 years building products inside startups, Verizon, enterprise innovation labs. Founding team member at Hum by Verizon (part of $612M acquisition by Hughes Telematics). Co-built Drive Safe & Save with State Farm (still one of the largest active telematics programs in the country). Product and technology lead on Hum by Verizon, scaled to millions of subscribers in under a year. Built Kazzam through Ampology in partnership with Party City — watched it get acquired.
- Chris Davis: 13 years as startup operator and CEO. Co-founder and CEO of Fansub, raised $1.5M from 4 institutional investors at $5M valuation. Mentors founders through Techstars, Act House, Gener8tor, and Black Ambition. MBA from Emory University, BA from Duke University (captain of the football team).
- Combined track record: $612M+ transaction value from companies Deep helped build to acquisition | 40+ MVPs launched through Intuitio Labs generating $225M+ in product value | $16M+ follow-on secured by studio-backed companies | $330K personal capital deployed before asking any LP for a dollar
- Thesis origin: "Southeast founders with the right instincts and the wrong infrastructure. RightShift is the infrastructure."
 
SLIDE 3 — INVESTMENT THESIS
Title: "A fund that builds before it bets"
Core thesis: "We believe the primary risk in pre-seed investing is product execution failure and that the Southeast is a massively under-invested and demonstrably growing region and that the Southeast has a critical supply gap in institutional product infrastructure. A fund that embeds product development inside the diligence process will consistently enter at better valuations, with lower failure rates."
- What we fund: Southeast U.S. B2B and B2C digital product companies at pre-seed stage.
- What we do NOT fund: Hardware, biotech, or deep tech requiring regulatory pathways. Fund I sharpens exclusively to digital consumer products where our studio has proven execution velocity and domain pattern recognition.
 
SLIDE 4 — THE PROBLEM AND THE OPPORTUNITY
- 42% of startups fail due to product challenges (CBInsights; https://www.cbinsights.com/research/report/startup-failure-reasons-top/) — not market timing, not team quality.
- Most VCs outsource diligence or evaluate from the outside. By the time they learn a portfolio company can't build, capital is already deployed and runway is burning.
- Southeast VC deployed $7.1B in 1H 2025 — a 33% year-over-year increase (BIP Ventures / PitchBook 2025; https://www.bipventures.vc/state-of-startups/2025). Capital is flowing in. Institutional product infrastructure is not.
 
SLIDE 5 — INVESTMENT STRUCTURE (Two-Tranche Model)
"Build First. Invest Second."
Phase 1 — Product Capital: $25K in embedded resources (our design, engineering, AI, and DevOps team working directly on their product). 8 weeks of real due diligence in the form of building. Studio credits delivered at $55/hr — a fraction of market rate.
Milestone Gate: Working MVP delivered. Customer traction demonstrated (revenue, engagement, or signed LOIs). Founder-market fit validated under real operating conditions. NO MILESTONE = NO GROWTH CAPITAL.
Phase 2 — Growth Capital: $75K cash — deployed only after milestones are hit.
Why this matters for LPs: We don't deploy majority of the capital until a company passes milestones.
 
SLIDE 6 — THE ECOSYSTEM (The Unfair Advantage)
- Intuitio Labs (Studio): 6 years building AI-powered products. Strategy, design, engineering, DevOps. $225M+ in product valuation created. 100% product completion rate. Over 65 Product Experts. The engine behind every investment decision.
- RightShift Accelerator: 8-week intensive, 100+ hours hands-on. Product validation → MVP → traction. Due diligence is built into the program — not added afterward. 100% Product Completion Rate.
- RightShift Ventures (Fund): $3M investment vehicle. Invests in ecosystem graduates and a structured outside track. $25K Product Capital + $75K Growth Capital. Capital deploys only after product is de-risked.
Most funds outsource 1–2 functions. We own all three — studio, accelerator, and fund — operating as a single integrated system.
 
SLIDE 7 — COMPETITIVE ADVANTAGE
"How RightShift Ventures Is Different"
- The Approach: Every fund competes for the same narrow pool of product-ready founders. We are building new supply. Tranche 1 is product engineering capital — turning pre-product founders into investable companies. We don't find the needle. We make more of them. (vs. YC/Techstars/500 Global: search and diversify, fixed supply, same playbook for 20 years)
- Capital efficiency due to embedded diligence: Accelerator shapes. Studio builds. Fund deploys — $25K first, $75K only when milestones clear. We have halved the capital at risk per write-off and increased concentration where it matters. No fund runs a milestone-gated two-tranche structure backed by a product studio. They all typically write the same check to everyone on day one.
- Power Law at $3M: One $10M exit at 7% moves the needle. Impossible at $100M. Capital only concentrates in companies that proved execution. Net MOIC top quartile: 2.5x+ · Net IRR top quartile: >15% · Write-off norm: 60–70%.
 
SLIDE 6 — COMPETITIVE POSITIONING
"Regional Access. Product Depth. No One Else Has Both."
- National accelerators (YC, Techstars): Program depth, no regional roots, no embedded studio
- Southeast generalists (BIP Capital, Valor): Regional access, no product-first filter
- RightShift Ventures: Only Southeast fund combining a proprietary product studio + active accelerator + investment vehicle
- Southeast VC deployed $7.1B in 1H 2025 — a 33% year-over-year increase — projected to reach $12B for full-year 2025, more than 23% above the 2018 baseline (BIP Ventures / PitchBook 2025)
 
SLIDE 8 — TRACK RECORD (Overview)
"The Infrastructure Existed Before the Fund"
$225M+ product valuation created for companies built by Intuitio Labs over six years.
100% product completion rate for all projects.
6 years dedicated to AI-powered products.
40+ MVPs launched across 11 industry verticals.
 
SLIDE 9 — TRACK RECORD (External Validation)
"We Selected Them First. Then the World Noticed."
- Okkanti → Techstars (institutional accelerator selection)
- Ballin.ai → Slauson & Co + a16z (Tier-1 VC co-investment post-RSV entry)
- Luxe AI → Gener8tor Pinnacle Innovation Accelerator (National accelerator selection)
- Video Pro Learning → Camelback Ventures Fellowship (Competitive national fellowship)
- VULVAi → 2025 NIA Start-Up Challenge Finalist (1 of 21 selected nationally, federal NIH recognition) + 2025 Echoing Green Fellow (selected from 4,200+ applicants) + media credibility in NYT, Oprah Daily, Forbes, Colbert Show; Dr. Uloko has lectured at Yale, Harvard, UCLA, USC.
 
SLIDE 10 — TEAM
"Built by Founders who got tired of watching it go wrong"
Deep Kalina, Founder & Managing Partner (deep.kalina@rightshift.co): 18+ years. 9 patents. Intuitio Labs founder. $225M+ product value. Held equity in In-Drive — sold to Verizon as Hughes Telematics for $612M. Co-built Drive Safe & Save with State Farm. Product and technology lead on Hum by Verizon. Built Kazzam through Ampology — acquired by Party City.
Chris Davis, Managing Partner (chris.davis@rightshift.co): 13 years tech startups. Venture-backed CEO — raised multiple millions from 4 institutional investors. MBA Emory. BA Duke. Mentor: Techstars, Act House, Gener8tor, Black Ambition.
Anastasia Nicole, Director of Strategic Initiatives & Investment Operations (anastasia.nicole@rightshift.co): Former Investment Principal, Techstars Atlanta & Managing Director, Techstars WaterTech. 50+ investments. $7M+ deployed. Southeast Regional Coach, Google Grow with Google.
Anna Cable, Network Development & Capital Strategy (anna.cable@rightshift.co): NEXUS Global Philanthropist Network. Brighter Investing. Renew VC. coLABS Gender Lens Portfolio Co-Founder. Village Capital Resource III. Women founder champion.
 
SLIDE 11 — GOVERNANCE
"Institutional Grade. Operator Owned"
- Conflict-of-interest protocol: One warehouse deal (Fansub) was co-founded by Chris Davis. Evaluated independently by Deep Kalina with Chris fully recused from the final investment decision. All future GP-adjacent investments follow the same documented protocol.
- Investment Committee: Decisions require consensus across three independent inputs: (1) product expert evaluation from the studio, (2) GP business and market judgment, and (3) external advisor perspective.
- Team scale: 65+ designers, engineers, and product experts in the studio — available as a resource for every portfolio company from day one. Not a directory. An active team.
- Builder-to-Picker Advantage: Deep built and scaled Hum by Verizon, Drive Safe & Save, Kazzam, and In-Drive — products that reached millions of users — as equity holder, product lead, and technology lead. Chris raised institutional capital as founding CEO of Fansub. Operator reps from inside companies that scaled. (First Round Capital; Kauffman Foundation)
 
SLIDE 12 — DEAL FLOW
"The Pipeline Is Built. Not Sourced."
- RightShift Accelerator Cohort: 8 companies currently in active investment evaluation — pre-vetted, product-validated, building with studio team in real time.
- Emory University Partnership: Signed partnership. Direct, durable pipeline of graduating founders. A signed agreement, not a soft introduction.
- Ecosystem infrastructure: Startup Atlanta (committee members) · Atlanta Tech Week (paid sponsors) · Venture Atlanta (paid sponsors) · ACT House · Techstars Atlanta · Culture House · Russell Innovation Center
- Total pipeline: 150+ companies per year
- Co-investors: Slauson & Co, Waka Flocka Flame, George Kaiser Family Foundation, Cortado Ventures, Oregon Sports Angels, FBomb Angels
 
SLIDE 13 — WAREHOUSE PORTFOLIO
"The Model in Motion Before You Commit"
Note: Entry dates and valuations are estimates pending final documentation. Current marks are GP estimates, not audited third-party valuations.
- Ballin.ai: Entry 2023, Entry Valuation $3M, Current Mark $5M. Slauson & Co + a16z co-investment. In Fund I warehouse at $5M valuation.
- Fansub: Entry 2022, Entry Valuation $2M, Current Mark $5M. $1.5M institutional VC secured. (Note: Chris Davis is co-founder; evaluated by Deep Kalina with Chris fully recused.)
- Stufinder: Entry 2023, Entry Valuation $1M, Current Mark $2M. Co-investment alongside Waka Flocka Flame.
- VulvAI: Entry 2024, Entry Valuation $5M, Current Mark TBD. Doctor-founded by Dr. Maria Uloko (board-certified urologist). 2025 NIA Start-Up Challenge Finalist. AI-powered platform for 26M+ Americans living with chronic vulvovaginal conditions. $2.6B and growing women's health VC market (SVB 2024). In Fund I warehouse at $5M entry valuation. Tranche 2 pending milestone gate.
 
SLIDE 14 — INVESTMENT STRUCTURE IN PRACTICE (Two Tracks, One Standard)
Studio Track (Ballin.ai, Sports-Tech/AI): Ballin entered through the studio. We built their Early Testable Product. Milestone gate cleared. Within one quarter — term sheets from Slauson & Co and a16z. We were on the cap table before the institutional money arrived. Both tranches deployed.
Q2 2024: Introduced via accelerator partner network. Q3 2024: Phase 1 executed — Product Capital deployed, RightShift team builds Ballin's Early Testable Product (ETP). Q4 2024: Milestone gate cleared. Ballin receives term sheets from Slauson & Co. and a16z. Q1 2025: Phase 2 Growth Capital deployed. Q2 2025–Present: $5M valuation. Outcome: Tier-1 co-investors. Better entry valuation. No information gap when capital deployed. In Fund I warehouse at $5M valuation. Co-investors: Slauson & Co · a16z (Term sheet)
Accelerator Track (VULVAi, Women's Health/AI): VULVAi entered through the accelerator. Dr. Maria Uloko built her product during the 8-week program. Tranche 1 deployed. Since then: 2025 NIA Start-Up Challenge Finalist, Echoing Green Fellow selected from 4,200+ applicants, featured in NYT, Forbes, Oprah Daily.
Cohort Entry: Dr. Maria Uloko accepted into RightShift Accelerator. 8-week program, 100+ hours hands-on. Milestone gate cleared. Post-graduation: 2025 NIA Start-Up Challenge Finalist (1 of 21 nationally). 2025 Echoing Green Fellow. Featured: NYT, Forbes, Oprah Daily, Colbert Show. Fund Entry: $5M valuation. Market: $2.6B and growing (SVB 2024). In Fund I warehouse at $5M entry valuation. Tranche 2 pending milestone gate.
 
SLIDE 15 — VALUE-ADD
"Capital Plus Infrastructure. Not Capital Instead of It."
1. 65 Product Experts: Continue as active product collaborators post-investment — real engineers, designers, and PMs working in the product. Not advisory calls.
2. Custom AI Agents: Purpose-built for portfolio monitoring and development acceleration. Every portfolio company inherits that infrastructure.
3. Strategic Partnerships: Microsoft, AWS, and Qburst provide enterprise-tier technical resources to portfolio companies at accessible cost structures unavailable to standalone pre-seed startups.
4. Talent Pipeline: Portfolio companies get first access to vetted designers, engineers, and PMs from the studio talent network.
 
SLIDE 16 — FUND ECONOMICS (see full model data below in FUND MODEL section)
"The Numbers Behind the Model"
Note: Deck explicitly shows GP Commit as 10% → $300,000. The $330K figure elsewhere in the deck reflects personal pre-fund capital deployed across 4 companies before Fund I.
 
SLIDE 17 — RETURN SCENARIOS (see full model data below in FUND MODEL section)
"What It Takes To Return The Fund"
Key return-to-fund context from this slide:
- Base case assumes 23 companies return $0. Each one costs $25K. A traditional fund pays $100K for the same outcome. 9 exits still deliver 2.76x Net — above Cambridge Associates top quartile. No unicorns needed.
- A single ~$43M exit at 7% ownership returns the entire fund. A $100M fund needs a $1.4B exit to do the same thing. Our fund size is a strategy.
- Just 7 small ($3M) and medium ($15M) exits return the fund and then some. That's the two-tranche model at work.
- Conservative assumes 75% of the portfolio returns nothing. LPs still get their capital back plus 66%.
 
SLIDE 18 — LP PARTNERSHIP
"What we owe you"
What You Should Hold Us To:
By End of Year 2: 70%+ of portfolio companies have a revenue-generating product live | At least 3 companies accepted into paid accelerators
By End of Year 4: At least 50% of portfolio companies have raised a follow-on round | At least 2 companies generating $500K+ ARR | Net DPI tracking toward 1.0×
What You Get as an LP:
- Quarterly portfolio updates
- 24/7 LP portal: financials, memos, performance data
- Deal flow exchange for co-investments
- First right of refusal on all follow-on rounds
- Warehouse deal participation before fund transfer — access to marked-up positions at entry cost
- Structured co-investment vehicles for outsized follow-on positions
How to Get Involved:
- Minimum LP commitment: $50,000
- LP maximum check size: $500,000
- First close target: Q2 2026
- Final close target: Q3 2026
- Contact: deep.kalina@rightshift.co | chris.davis@rightshift.co
 
== FUND MODEL DATA (Source: Rightshift_Ventures-Fund_I_Model_-2Tranches-03092026-4.xlsx) ==
 
SECTION 1 — CAPITAL & FUND STRUCTURE
- Fund Size: $3,000,000
- GP Commit: 10% = $300,000 (5× the ILPA recommended minimum of 1–2%)
- Carried Interest: 20% (European waterfall structure — carry paid only after LPs receive all committed capital back)
- Management Fee: 2.0% per year of committed capital (Years 1–8), 0% in Years 9–10
- Fund Duration: 10 years + 2-year extension option
- New Investment Period: 4 years
- Hurdle Rate: None (no preferred return hurdle in the model — disclosed to LPs)
- Recycled Capital: 0% (no recycling in current model)
 
SECTION 2 — FUND EXPENSES
One-Time Organizational Expenses:
- State domicile filing fees: $2,500
- Blue sky fee: $1,000
- Form ADV filing: $500
- Legal formation fee: $20,000
- TOTAL One-Time: $24,000
 
Annual Recurring Operational Expenses:
- Annual Tax Prep: $5,000
- CPA/Accountant fee: $12,000
- Legal Operations: $7,500
- KYC/AML and compliance: $2,500
- Treasury/Banking Fee: $2,500
- TOTAL Annual Recurring: $29,500/year
 
Management Fees (10-year schedule):
Years 1–8: 2% × $3M = $54,000/year per year
Years 9–10: 0% (fee drops to zero)
Total management fees over fund life: ~$432,000
 
Total Fees & Expenses (management fees + all org/operational expenses): $751,000
Invested Capital (deployed to companies): $2,249,000
% Deployed to Companies: 75.0%
 
SECTION 3 — PORTFOLIO CONSTRUCTION (Two-Tranche Model)
- Original target companies (cohort 1): 20 companies
- New companies via redeployment: 12 companies
- Total portfolio companies: 32 companies
- Tranche 1 per company (Product Capital as studio credits): $25,000 at $55/hr
- Tranche 2 per company (Growth Capital, milestone-gated): $75,000 cash
- Milestone pass rate: 40% (only 40% of companies receive Tranche 2)
- Average equity ownership: 7% per company
- LP maximum check size: $500,000
 
How the two-tranche math works:
- Of 20 original companies: 8 pass milestone (receive full $100K = $25K + $75K), 12 receive only $25K (Tranche 1 only)
- Of 12 new companies via redeployment: 5 pass milestone (full $100K), 7 receive only $25K
- Effective average deployed per company: $2,249,000 ÷ 32 = ~$70,250
- This means LPs get diversification across 32 companies but the fund doesn't risk the full $100K on every company — only the ones that prove it.
 
Per-deal transaction costs (from the fund, not management fees):
- Legal fees: $7,500/deal
- Due diligence: $7,500/deal
- Travel/entertainment: $500/deal
- Consulting fee: $1,000 × 30% of deals = $300/deal average
- Deal closing costs: $750/deal
 
SECTION 4 — RETURN SCENARIOS (from Return Scenarios sheet)
All three scenarios assume: 7% equity ownership, 32 companies, $2,249,000 invested capital
 
SCENARIO 1 — CONSERVATIVE:
Exit assumptions:
- Writeoffs (returns $0): 75% of portfolio = 24 companies
- Small exits ($2M company value × 7% = $140K return): 13% of portfolio = ~4 companies. Exit multiple: ~1.4x
- Medium exits ($10M company value × 7% = $700K return): 7% of portfolio = ~2 companies. Exit multiple: ~7x
- Large exits ($25M company value × 7% = $1.75M return): 5% of portfolio = ~2 companies. Exit multiple: ~17.5x
Fund Performance:
- Proceeds: ~$5,460,000
- Carried Interest: ~$492,000
- LP Distributions: ~$4,968,000
- Gross MOIC: 2.43x
- Net MOIC / TVPI: 1.66x (LPs get their capital back + 66%)
- Gross IRR: ~13.6% (estimated)
- Net IRR: ~8.7% (vs. >12% median benchmark)
- DPI: 1.66x
- Holding Period: ~6.1 years
Benchmark Context: Conservative Net MOIC (1.66x) is at the median range (1.5–2.0x). Even with 75% writeoffs, LPs still get their capital back plus 66%. This is the floor.
 
SCENARIO 2 — BASE:
Exit assumptions:
- Writeoffs (returns $0): 72% of portfolio = 23 companies
- Small exits ($3M company value × 7% = $210K return): 12% = ~4 companies
- Medium exits ($15M company value × 7% = $1.05M return): 10% = ~3 companies
- Large exits ($40M company value × 7% = $2.8M return): 6% = ~2 companies
Fund Performance:
- Proceeds: ~$8,272,000 (approx, from pitch deck)
- Carried Interest: ~$1,318,000
- LP Distributions: ~$8,272,000 (gross) — net to LPs approximately $6,954,000 after carry
- Note: Pitch deck shows LP Distributions of $8,272,000 and Carried Interest of $1,318,000
- Gross MOIC: 4.26x
- Net MOIC / TVPI: 2.76x (above Cambridge Associates top quartile of 2.5x+)
- Gross IRR: ~27.3% (top quartile)
- Net IRR: ~18.4% (vs. >15% top quartile benchmark — BASE exceeds top quartile)
- DPI: 2.76x
- Holding Period: ~6.0 years
Benchmark Context: Base Net MOIC (2.76x) exceeds top-quartile benchmark. 9 exits still deliver 2.76x Net. No unicorns needed. $40M exits happen every year in the Southeast. A $100M fund can't feel them. We can.
 
SCENARIO 3 — BEST CASE:
Exit assumptions:
- Writeoffs: 45% of portfolio = ~14 companies
- Small exits ($5M × 7% = $350K): 15% = ~5 companies
- Medium exits ($25M × 7% = $1.75M): 15% = ~5 companies
- Large exits ($75M × 7% = $5.25M): 25% = ~8 companies
Fund Performance:
- Gross MOIC: significantly higher (single large exit at $75M moves needle dramatically at $3M fund size)
- Net MOIC / TVPI: top-decile outcome
- DPI: 8.9x+ (exceptional)
- Best case Gross IRR modestly lower due to longer holding on large exits (~15.1 yr weighted hold)
Benchmark Context: Best case DPI of 8.9x is exceptional. One company in the portfolio returning ~$5M at 7% ownership = $75M exit. Not common but well within range of pre-seed outcomes at this ownership level.
 
SECTION 5 — INDUSTRY BENCHMARKS
Source: Cambridge Associates 2024, ILPA 2023, NVCA, Kauffman Foundation, PitchBook
(All benchmarks are for emerging managers, pre-seed/seed focus, fund size $1M–$10M)
 
Gross MOIC benchmark: 2.0x–4.0x top quartile. RSV Base (4.26x) exceeds top-quartile.
Net MOIC benchmark: 1.5x–2.0x median, 2.5x+ top quartile, 5.0x+ top decile. RSV Base (2.76x) is top quartile.
Gross IRR benchmark: >20% top quartile, >30% top decile. RSV Base Gross IRR (~27.3%) is top quartile.
Net IRR benchmark: >12% median, >15% top quartile, >25% top decile. RSV Base Net IRR (18.4%) is top quartile. RSV Conservative (8.7%) is below median — reflects high fee load on small fund.
DPI benchmark: 0.5x–1.5x at fund end (varies widely). All RSV scenarios exceed 1.0x DPI.
Writeoff rate benchmark: 60–70% industry norm for pre-seed. RSV Conservative (75%) is conservative by design. RSV Base (72%) is at upper end of industry norm. Product studio and accelerator filter are designed to bring this below industry average.
Equity ownership benchmark: 5–8% at $100K check (accelerator/pre-seed). RSV at 7% is within standard range, slightly above 6% average. Defensible given product-studio de-risking.
Number of portfolio companies benchmark: 15–25 for small funds. RSV at 32 is above benchmark via two-tranche structure — $25K Tranche 1 buys full 7%; only 40% receive Tranche 2.
Total fee load: 15–25% for micro funds ($1M–$10M). RSV management fees = 16.2% of committed capital. Total fee load (incl. org/operational) is higher at ~28.5% — reflects lean formation costs and annual operations at sub-$5M fund scale. 75% of committed capital deployed into companies.
GP count benchmark: 2–3 GPs per fund typical for $3M–$10M. RSV has 2 GPs with AI-augmented capacity for portfolio monitoring, LP reporting, and diligence support — enabling two GPs to manage a 32-company portfolio at institutional quality.
Structural MOIC advantage: Small funds (<$10M) historically outperform large funds on MOIC (Kauffman Foundation). A $3M fund needs only one $10M+ exit at 7% ownership to return 2x+ net. This math is impossible for a $100M fund.
 
SECTION 6 — LP ECONOMICS EXAMPLES
If an LP invests $100,000:
- LP's share of fund: $100K / $2.7M LP capital = ~3.7%
- Conservative (1.66x Net): LP receives ~$166,000 back (66% return over ~6 years)
- Base (2.76x Net): LP receives ~$276,000 back (176% return over ~6 years)
- Best Case: Multiples of the above, proportionally
 
If an LP invests $500,000 (maximum):
- LP's share of fund: $500K / $2.7M LP capital = ~18.5%
- Conservative (1.66x Net): LP receives ~$830,000 back
- Base (2.76x Net): LP receives ~$1,380,000 back
- Best Case: Proportionally higher
 
SECTION 7 — GLOSSARY (for LP questions about terminology)
- LP (Limited Partner): Investor in the fund. Liability limited to capital invested.
- GP (General Partner): Fund manager. Day-to-day operations, investment decisions, portfolio management.
- Committed Capital: Total fund size. LP + GP commit combined.
- Called Capital / PIC: Capital actually drawn from LPs. At fund close = committed capital. PIC ratio = 1.0x.
- GP Commit: % of committed capital contributed by GPs. Industry standard 1–3%. RSV commits 10% (5× standard).
- Management Fee: Annual fee (2% per year of committed capital) paid to GPs for fund operations.
- Carry / Carried Interest: GP share of gains above committed capital. RSV = 20%. European waterfall = paid only after LPs get all committed capital back.
- Invested Capital: Committed capital minus all fees and expenses, plus recycled capital. Actually deployed into companies.
- Proceeds: Cash returned from exits (M&A, IPO, secondary). Gross before carry and fees.
- Distributions: Net cash paid to LPs. Proceeds minus carried interest.
- Gross MOIC: Proceeds / Invested Capital. Pre-fees, pre-carry.
- Net MOIC / TVPI: Distributions / Paid-In Capital. The LP's actual return multiple.
- DPI: Distributed to Paid-In Capital. Cash returned to LPs / LP capital called.
- TVPI: Total Value to Paid-In Capital = DPI + RVPI. Primary performance metric for VC funds.
- IRR: Internal Rate of Return. Accounts for time value of money. Gross IRR = pre-fees. Net IRR = LP's actual IRR.
- Power Law: VC return distribution: 40–60% writeoffs, small gains from most survivors, 1–2 outliers drive fund returns.
- Pro Rata: Right to maintain ownership % in future financing rounds. Protects stakes in winners.
- European Waterfall: Carry paid only after LPs receive back ALL committed capital. Standard for US VC funds.
 
== LP FAQ — OFFICIAL SOURCE (Right Shift Fund I FAQ, Notion) ==
 
Q: What's the target fund size, and why not larger?
A: $3M. Deliberate choice. At this size, one or two real breakouts return the fund — a single $15M exit at 7% ownership more than covers invested capital. Going larger hurts returns: more companies than we can stay close to, or bigger checks with more risk per company. We're first-time fund managers — $3M is closeable, deployable with discipline, and builds a real track record. We put 10% of our own money in (5× industry standard) — we're as motivated as any LP to make returns work.
 
Q: Your MOIC looks strong but your IRR seems low — why?
A: It comes down to time. MOIC tells you how much you multiply your money. IRR tells you how fast. We're pre-seed investors — we're getting in early and holding through real value creation. That takes 6–7 years, not 3. A fund showing high IRR at pre-seed is usually assuming unrealistically quick exits, which is misleading. In our base case you get 2.76x your money back — whether that takes 6 years or 10 changes the IRR significantly, but it doesn't change the fact that you nearly tripled your money. Our base case Net IRR is ~18.4%, which clears the 15% threshold most institutional LPs require. The MOIC of 2.76x is top quartile. For a 10-year horizon investor — which is who this fund is designed for — how much you get back matters more than how fast the spreadsheet annualizes it.
 
Q: Why is your modeled writeoff rate (72%) higher than the 60–70% industry norm?
A: Because we want the base case to hold up even if our de-risking thesis is completely wrong. If the accelerator and product studio produce zero improvement over a typical pre-seed portfolio, we still return 2.76x net to LPs. If the de-risking works as intended and writeoffs come in at or below the industry norm, base case returns are higher than modeled. The 72% is the pessimistic assumption, not the expected one.
 
Q: Are follow-on returns included in your projections?
A: No. The $474,000 follow-on reserve is not modeled in any scenario. All three return figures — conservative 1.66x, base 2.76x, best 14.2x — exclude follow-on upside entirely.
 
Q: What is the effective check size — $100K or $70K?
A: Both are accurate but measure different things. $100K is the committed check per company if milestones are hit. $70K is the effective average actually deployed across the full 32-company portfolio, accounting for companies that only receive Tranche 1.
 
Q: What happens if the 40% milestone pass rate is wrong?
A: The pass rate drives all portfolio construction — company counts, capital deployed, reserve size, and redeployment. Higher pass rate: more T2 capital called, reserve shrinks, fewer new companies funded. Lower pass rate: reserve grows, more capital available for redeployment. The 40% is deliberately conservative — we expect it to be higher given studio filtering.
 
Q: Are the 12 redeployment companies guaranteed?
A: No. Pipeline-dependent. If deal flow underperforms during the investment period, the portfolio may be closer to 20 than 32. The $675,000 earmarked for those 12 companies would shift to the reserve.
 
Q: How are your IRR figures calculated?
A: Standard methodology: MOIC raised to the power of 1 divided by the weighted average holding period, minus 1. Actual IRR will vary because exits happen at different times. These figures are directionally accurate for LP planning, not precise cash-flow IRR.
 
Q: Why do management fees drop to zero in years 9 and 10?
A: Active deployment and portfolio management is concentrated in years 1–8. Years 9–10 are wind-down and distribution. Charging full fees through year 10 on a largely realized portfolio is not LP-aligned. Total fees are $432,000 over fund life vs. $540,000 under a flat 2% structure — a $108,000 LP-friendly reduction.
 
Q: What is the GP commit and why does it matter?
A: $300,000 — 10% of the fund. Management fees are charged on $2.7M, not $3M, because the GP commit is excluded from the fee base. The ILPA recommended minimum is 1–2%. We are at 5× that minimum. Our capital is at risk alongside yours under identical terms.
 
Q: Is recycled capital included in the model?
A: No. Recycled capital is zero. All exits are distributed or taken as carry. The follow-on reserve is a separate, explicitly reserved pool — not recycled proceeds.
 
Q: How should we read the best case scenario?
A: As a mathematical upper bound, not a fund target. It requires 8 large exits at $75M valuations at 7% ownership. The number we underwrite to and manage the fund against is the base case: 2.76x net MOIC, 18.4% net IRR.
 
Q: What does a company need to achieve to unlock Tranche 2?
A: A company moves from T1 ($25K) to T2 ($75K) by demonstrating it has earned the capital, not just asked for it. Three gates — a company must hit at least two of three:
1. Revenue or paid traction: early revenue or paying customers — not just users, not just a waitlist. Pre-seed milestone best practice requires measurable criteria: "$50K in MRR" is a milestone; "gain traction" is not.
2. Outside capital raised: a SAFE, angel round, or acceptance into another accelerator — an independent third-party signal of conviction. 74% of Techstars companies raise follow-on capital within three years; RSV portfolio companies should be tracking ahead of that benchmark.
3. Meaningful moat evolution: strengthened defensibility — sharpened IP, deeper distribution channel, exclusive partnership, or product improvements harder for a competitor to replicate. Not about pivoting — about widening the gap.
All three is a strong signal; none of three means T2 capital stays in reserve. The accelerator and studio give us an embedded view of each company — we're not making the T1→T2 call from the outside.
 
Q: What happens to the $25K Tranche 1 for companies that don't pass the milestone gate?
A: T1 is $25K deployed to the founder as studio credits, redeemable for Intuitio Labs product services at $55/hr — a fraction of market rate. Every dollar goes toward building the product. Beyond the evaluation function, this solves a real problem: most founders burn a significant portion of their raise just getting to a working prototype. RSV founders don't — they enter the growth phase with their capital largely intact, which means more runway, less dilution pressure, and a stronger position heading into their next raise. For companies that don't advance, $25K is the full cost of that evaluation. The $75K Growth Capital never deploys.
 
Q: Why deploy capital as studio credits rather than just writing a check?
A: Most pre-seed founders don't fail for lack of cash — they fail because the cash goes to building the wrong thing. 75% of venture-backed startups fail, and 82% of businesses that closed in 2023 did so because of cash flow problems — not because they ran out of ideas. Runway burns fastest at the prototype stage, before anyone knows if the product works. Market rate for MVP development from an institutional-quality agency runs $75,000 to well over $150,000. A founder who spends that on a prototype before raising has consumed most of their pre-seed capital before a single customer has touched the product. RSV's $25K in studio credits at $55/hr gets founders to a working MVP without torching their runway. The structure isn't a constraint — it's capital efficiency by design.
 
Q: Your conservative IRR is 8.7% — below the CA median. Why should an LP accept emerging manager risk for below-median downside?
A: Because the conservative scenario isn't a typical downside — it assumes 24 of 32 companies return nothing (a 75% failure rate). Most funds don't stress-test to that level. The conservative scenario combines three compressing factors: 75% write-off rate (worse than industry norm), small exit values ($25M ceiling on large exits), and longer holding periods. IRR is time-sensitive — when exits are small and slow, IRR compresses even when MOIC is positive. Under those conditions, the fund still returns LP capital plus 66% (1.66x Net MOIC). The 8.7% IRR is the cost of modeling an honest worst case, not evidence of structural underperformance. The base case delivers 2.76x Net MOIC and 18.4% Net IRR — above Cambridge Associates top-quartile threshold on both measures.
 
Q: What evidence exists that embedded product support actually improves outcomes?
A: In 2023, over 70% of seed investors required a working MVP before committing funds. 60% of pre-seed companies fail to reach Series A. The execution gap between having capital and having a product is where most pre-seed investments die. RSV compresses that gap by embedding the build capability directly into the investment. The studio has launched 40+ products with a 100% completion rate — that operational track record is the evidence applied to every company in the portfolio from day one.
 
Q: How does a $3M fund generate power law returns? / How do you get to 32 companies? / How does the two-tranche structure change the portfolio math?
A: Power law dynamics require a minimum of 20–30 portfolio companies to have a reliable chance of capturing an outlier. A traditional $3M fund writing $100K flat checks can back only ~22 companies after fees — below that threshold. Our two-tranche structure gets us to 32 on the same invested capital. That is the entire construction logic of the fund. Here's how: 20 companies are funded initially. 40% (8 companies) clear the milestone gate and receive Tranche 2 ($75K). The 60% that don't pass stop at $25K, freeing $75K per company for immediate recycling. That freed capital funds 12 additional companies. Total: 32 companies, $1.775M deployed across T1 and T2, $474K held as reserve. The 30-company threshold is supported by Skalata Ventures portfolio construction research and AngelList data on 1,808 investments — both show that with fewer than ~20-30 companies, the probability of a power law outlier landing in your portfolio drops materially.
 
Q: What is the fund's capital deployment schedule?
A: $2.249M in invested capital deployed across a 4-year investment period into 32 companies. Every company enters at $25K. 40% clear the milestone gate and receive the full $75K Tranche 2. The 60% that don't stop at $25K — and that freed $75K per company goes back into the pipeline to fund the next entry check. The structure acts as a natural pace governor: Tranche 1 checks are small enough to move quickly, and capital doesn't concentrate until a company has demonstrated MVP, traction, and team readiness. That sequencing means a rolling, real-time pipeline rather than batched investments by quarter. The 4-year investment window is matched to the 10-year fund duration with a 2-year extension baked in.
 
Q: What is the management fee structure?
A: 2% of committed capital per year. $432K in total management fees over 10 years — standard by ILPA and NVCA benchmarks (2.0%–2.5% industry range). Fees drop to 0% in Years 9–10. Total fees are $432K versus $540K under a flat 2% structure — a $108K LP-friendly reduction.
 
Q: What are the total fees as a % of committed capital?
A: $751K total — approximately 25% of the $3M fund, at the upper edge of the 15%–25% range for micro-funds. $24K is one-time org cost. $295K covers 10 years of recurring operations. $432K is management fees. 75 cents of every committed dollar is deployed into companies.
 
Q: Is there a hurdle rate?
A: The model does not include a preferred return hurdle. This is disclosed. At a $3M fund size with a 10-year duration, the fee load and structural conservatism of the deployment model function as the practical alignment mechanism. LPs with a preferred return requirement should raise this directly with the GPs.
 
Q: How much are the GPs putting in?
A: $300,000 — 10% of the $3M fund. Approximately 5× the ILPA recommended minimum of 1–2%. The GP commit is excluded from the management fee base per the ILPA fee transparency framework. Our capital is at risk alongside yours under identical terms. Note: the $330K figure that appears in some materials refers to personal capital deployed across 4 companies before Fund I was formally constituted — it is not the Fund I GP commit.
 
Q: How do you handle a 32-company portfolio with two GPs?
A: Portfolio monitoring, LP reporting, founder check-ins, and diligence support are AI-augmented — enabling institutional-grade operations without additional headcount. This is a structural capacity decision, not a cost-cutting one. The Kauffman Foundation and NVCA both cite 2–3 GPs as the benchmark for emerging funds in the $3M–$10M range.
 
Q: Where do the return scenarios sit relative to Cambridge Associates benchmarks?
Conservative: 1.66x Net MOIC, 8.7% Net IRR — at median Net MOIC; below median Net IRR.
Base: 2.76x Net MOIC, 18.4% Net IRR — top quartile on both metrics.
Best: 14.2x Net MOIC, 50.1% Net IRR — top decile.
Source: Cambridge Associates 2024, ILPA Emerging Manager Study 2023.
 
Q: What exit size assumptions drive the base case?
A: Small exits: $3M company value × 7% = $210K. Medium exits: $15M × 7% = $1.05M. Large exits: $40M × 7% = $2.8M. Four exits across those tiers, with 23 writeoffs out of 32 companies, produce ~$9.59M in gross proceeds against $2.249M invested — 4.26x Gross MOIC, 2.76x Net MOIC after fees and carry.
 
== IMPORTANT CONTEXT ==
- GP Commit is $300,000 — 10% of the $3M fund. The cover slide now correctly shows "$300K personal skin in the game." The $330K figure that appeared on earlier deck cover slides referred to personal capital deployed across 4 companies before Fund I was formally constituted. The Notion FAQ still shows $330K in one Q&A — that is an error in the FAQ; the authoritative number is $300,000 per the Fund Economics slide and fund model.
- Fansub conflict-of-interest is fully disclosed: Chris Davis co-founded Fansub. Deep Kalina evaluated it independently with Chris fully recused. All future GP-adjacent investments follow the same protocol. Note: Chris Davis is now described in the deck as "co-founder and CEO of a venture-backed startup" (Fansub name omitted from the narrative) — this is intentional.
- All warehouse portfolio marks (Ballin, Fansub, Stufinder, VulvAI) are GP estimates, not audited third-party valuations.
- The fund has not yet held a first close. First close is targeted for Q2 2026.
- Fund I is structured as a Reg D 506(b) or 506(c) vehicle (confirm with GPs). Accredited investors only.
 
Always respond in a direct, confident, numbers-fluent tone. Be transparent about what is and isn't modeled. When asked about something not in the materials, acknowledge it and offer to connect the LP with the team. Keep responses concise but complete. Do not add disclaimers to every message — this is a sophisticated investor audience. End answers with the relevant contact info (deep.kalina@rightshift.co or chris.davis@rightshift.co) only when the LP seems ready to take a next step or asks about committing.
`;
 
const SUGGESTED_QUESTIONS = [
  "What's the target return for LPs?",
  "How does the two-tranche structure work?",
  "Walk me through the portfolio construction",
  "What does it take to return the fund?",
  "How does $100K invested grow in each scenario?",
  "What's the GP commit?",
  "Who are the fund managers?",
  "What's the minimum LP commitment?",
];
export default function App() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Welcome to the RightShift Ventures Fund I LP Portal. I'm here to answer any questions about the fund — structure, returns, team, portfolio, or how to get involved. What would you like to know?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;

    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: FUND_CONTEXT,
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "I couldn't process that. Please try again.";
      setMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch {
      setMessages([...newMessages, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setLoading(false);
  };

  const showSuggestions = messages.length === 1;

  return (
    <div style={{
      fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: "#1a1919",
      height: "100vh",
      minHeight: "100vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      color: "#e8e4dc",
    }}>
      {/* Header — fixed */}
      <div style={{
        flexShrink: 0,
        borderBottom: "1px solid #2a2820",
        padding: "20px 32px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        background: "#0d0d0a",
        flexWrap: "wrap",
      }}>
        <img src="/rightshift-logo.png" width="36px" alt="" />
        <div>
          <div style={{ fontSize: "15px", fontWeight: "600", fontFamily: '"Neue Haas Grotesk", Helvetica, Arial, sans-serif', letterSpacing: "0.04em", color: "#ffffff" }}>
            RightShift Ventures
          </div>
          <div style={{ fontSize: "11px", color: "#7a7468", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
            Fund I · LP Information Portal
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            fontSize: "11px",
            color: "#c9a84c",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            border: "1px solid #3a3420",
            padding: "4px 10px",
            borderRadius: "2px",
          }}>
            Confidential
          </div>
          <a
            href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0UI5oTHWlH5oOzqJoVdP-iZBjW1foSE-uV_CJFlSa8RpPotnhw5oB3qOJ_kVtYB0_N9wYUEYEr"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: "11px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "6px 12px",
              borderRadius: "16px",
              border: "1px solid #7b37db",
              color: "#f5f0ff",
              background: "linear-gradient(135deg, #7b37db, #4B1FA8)",
              textDecoration: "none",
              fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
          >
            Schedule a Call
          </a>
        </div>
      </div>

      {/* Founders strip — fixed */}
      <div
        className="founders-strip"
        style={{
          flexShrink: 0,
          margin: "0 auto",
          width: "100%",
          maxWidth: "780px",
          padding: "16px 32px",
          boxSizing: "border-box",
        }}
      >
        <div
          className="founders-strip-card"
          style={{
            padding: "18px 20px",
            borderRadius: "12px",
            border: "1px solid rgba(123,55,219,0.5)",
            background:
              "linear-gradient(135deg, rgba(123,55,219,0.35), rgba(75,31,168,0.7))",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div
            className="founders-strip-label"
            style={{
              fontSize: "11px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#d6d0c4",
              textAlign: "center",
              marginBottom: "6px",
            }}
          >
            Fund Leadership
          </div>
          <div
            className="founders-strip-people"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "12px 16px",
            }}
          >
            <div className="founder-block" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src="/deep.png"
                alt="Deep Kalina"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "999px",
                  objectFit: "cover",
                  border: "1px solid #3a3420",
                }}
              />
              <div>
                <div className="founder-name" style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.03em" }}>
                  Deep Kalina
                </div>
                <div className="founder-role" style={{ fontSize: "11px", color: "#a09890", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0" }}>
                  Founder &amp; Managing Partner
                </div>
                <a
                  href="mailto:deep.kalina@rightshift.co"
                  style={{ fontSize: "10px", color: "#8f8678", letterSpacing: "0.03em", textDecoration: "none", marginTop: "0", lineHeight: "1.3", display: "block" }}
                >
                  deep.kalina@rightshift.co
                </a>
              </div>
            </div>

            <div className="founder-block" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src="/chris.png"
                alt="Chris Davis"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "999px",
                  objectFit: "cover",
                  border: "1px solid #3a3420",
                }}
              />
              <div>
                <div className="founder-name" style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.03em" }}>
                  Chris Davis
                </div>
                <div className="founder-role" style={{ fontSize: "11px", color: "#a09890", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0" }}>
                  Managing Partner
                </div>
                <a
                  href="mailto:chris.davis@rightshift.co"
                  style={{ fontSize: "10px", color: "#8f8678", letterSpacing: "0.03em", textDecoration: "none", marginTop: "0", lineHeight: "1.3", display: "block" }}
                >
                  chris.davis@rightshift.co
                </a>
              </div>
            </div>

            <div className="founder-block" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src="/anastasia.png"
                alt="Anastasia Nicole"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "999px",
                  objectFit: "cover",
                  border: "1px solid #3a3420",
                }}
              />
              <div>
                <div className="founder-name" style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.03em" }}>
                  Anastasia Nicole
                </div>
                <div className="founder-role" style={{ fontSize: "11px", color: "#a09890", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0" }}>
                Director of Strategic Initiatives & Investment Operations
                </div>
                <a
                  href="mailto:anastasia@rightshift.co"
                  style={{ fontSize: "10px", color: "#8f8678", letterSpacing: "0.03em", textDecoration: "none", marginTop: "0", lineHeight: "1.3", display: "block" }}
                >
                  anastasia.nicole@rightshift.co
                </a>
              </div>
            </div>

            <div className="founder-block" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src="/anna.png"
                alt="Anna Cable"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "999px",
                  objectFit: "cover",
                  border: "1px solid #3a3420",
                }}
              />
              <div>
                <div className="founder-name" style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "0.03em" }}>
                  Anna Cable
                </div>
                <div className="founder-role" style={{ fontSize: "11px", color: "#a09890", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0" }}>
                  Network Development &amp; Capital Strategy
                </div>
                <a
                  href="mailto:anna@rightshift.co"
                  style={{ fontSize: "10px", color: "#8f8678", letterSpacing: "0.03em", textDecoration: "none", marginTop: "0", lineHeight: "1.3", display: "block" }}
                >
                  anna.cable@rightshift.co
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat messages — scrollable */}
      <div
        className="chat-shell"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "24px 32px 16px",
          maxWidth: "780px",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
          background: "#1a1919",
          borderLeft: "1px solid #262626",
          borderRight: "1px solid #262626",
        }}
      >
        {messages.map((m, i) => (
          <div key={i} style={{
            marginBottom: "28px",
            display: "flex",
            flexDirection: "column",
            alignItems: m.role === "user" ? "flex-end" : "flex-start",
          }}>
            {m.role === "assistant" && (
              <div style={{
                fontSize: "10px",
                color: "#c9a84c",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                marginBottom: "8px",
                paddingLeft: "2px",
              }}>RSV Fund I Assistant</div>
            )}
            <div style={{
              maxWidth: "88%",
              padding: m.role === "user" ? "12px 18px" : "20px 24px",
              background: m.role === "user"
                ? "linear-gradient(135deg, #1e1c14, #2a2618)"
                : "#111108",
              border: m.role === "user"
                ? "1px solid #3a3420"
                : "1px solid #1e1c14",
              borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "2px 12px 12px 12px",
              fontSize: "15px",
              lineHeight: "1.75",
              color: m.role === "user" ? "#d4cfc4" : "#e0dbd0",
              whiteSpace: "pre-wrap",
            }}>
              {m.content}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#7a7468",
            fontSize: "13px",
            paddingLeft: "2px",
          }}>
            <div style={{ display: "flex", gap: "4px" }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: "5px",
                  height: "5px",
                  background: "#c9a84c",
                  borderRadius: "50%",
                  animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  opacity: 0.6,
                }} />
              ))}
            </div>
            Composing response...
          </div>
        )}

        {showSuggestions && !loading && (
          <div style={{ marginTop: "8px" }}>
            <div style={{
              fontSize: "11px",
              color: "#5a5650",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: "14px",
            }}>Common questions</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <button key={i} onClick={() => sendMessage(q)} style={{
                  background: "transparent",
                  border: "1px solid #2a2820",
                  borderRadius: "20px",
                  padding: "7px 16px",
                  fontSize: "13px",
                  color: "#a09890",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.15s ease",
                }}
                  onMouseEnter={e => {
                    e.target.style.borderColor = "#c9a84c";
                    e.target.style.color = "#c9a84c";
                  }}
                  onMouseLeave={e => {
                    e.target.style.borderColor = "#2a2820";
                    e.target.style.color = "#a09890";
                  }}
                >{q}</button>
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input — fixed at bottom */}
      <div style={{
        flexShrink: 0,
        borderTop: "1px solid #1e1c14",
        padding: "20px 32px",
        background: "#0d0d0a",
        maxWidth: "780px",
        width: "100%",
        margin: "0 auto",
        boxSizing: "border-box",
      }}>
        <div style={{
          display: "flex",
          gap: "12px",
          alignItems: "flex-end",
        }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about fund structure, returns, team, or how to invest..."
            rows={1}
            style={{
              flex: 1,
              background: "#111108",
              border: "1px solid #2a2820",
              borderRadius: "8px",
              padding: "12px 16px",
              fontSize: "14px",
              color: "#e8e4dc",
              fontFamily: "inherit",
              resize: "none",
              outline: "none",
              lineHeight: "1.5",
              maxHeight: "120px",
              overflow: "auto",
              transition: "border-color 0.15s",
            }}
            onFocus={e => e.target.style.borderColor = "#c9a84c"}
            onBlur={e => e.target.style.borderColor = "#2a2820"}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            style={{
              background: input.trim() && !loading
                ? "linear-gradient(135deg, #c9a84c, #a8881c)"
                : "#1e1c14",
              border: "none",
              borderRadius: "8px",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: input.trim() && !loading ? "pointer" : "not-allowed",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={input.trim() && !loading ? "#0a0a08" : "#3a3830"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <div style={{
          marginTop: "10px",
          fontSize: "11px",
          color: "#3a3830",
          textAlign: "center",
          letterSpacing: "0.05em",
        }}>
          This assistant has access to the RSV Fund I pitch deck, FAQ, and fund model data · For official documents contact deep.kalina@rightshift.co
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        textarea::placeholder { color: #3a3830; }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2a2820; border-radius: 2px; }

        @media (max-width: 640px) {
          .chat-shell {
            padding: 20px 16px;
            border-left: none;
            border-right: none;
          }
          .founders-strip {
            padding: 6px 12px 8px;
          }
          .founders-strip-card {
            flex-direction: column;
            align-items: flex-start;
            padding: 10px 12px;
            gap: 4px;
          }
          .founders-strip-label {
            min-width: auto;
            margin-bottom: 2px;
            font-size: 10px;
            letter-spacing: 0.12em;
          }
          .founders-strip-people {
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px 10px;
          }
          .founders-strip-people img {
            width: 32px;
            height: 32px;
          }
          .founders-strip-people > div {
            min-width: 0;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            text-align: center;
          }
          .founders-strip-people > div > div:last-child {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0;
          }
          .founder-block .founder-name {
            font-size: 12px !important;
          }
          .founder-block .founder-role {
            font-size: 10px !important;
          }
        }
      `}</style>
    </div>
  );
}
